import type React from 'react';
import type { KeycloakUser } from '@/api/users';
import { Badge } from '@/components/ui/badge';
import { ACCESS_LEVEL_LABEL, resolveAccessLevel } from '@/lib/authz';
import { formatDate } from '@/lib/utils';
import { type ColumnConfig, DataTable, type DataTableResponse } from './data-table';

interface Props {
  data?: DataTableResponse<KeycloakUser> | undefined;
  isLoading?: boolean;
  error?: string | null;
  onSortChange?: (sortBy: string, order: 'asc' | 'desc') => void;
  onPageChange?: (page: number) => void;
  onUserClick?: (user: KeycloakUser) => void;
  searchInput?: React.ReactNode;
}

export const UsersTable: React.FC<Props> = ({
  data,
  isLoading = false,
  error = null,
  onSortChange,
  onPageChange,
  onUserClick,
  searchInput,
}) => {
  const columns: ColumnConfig<KeycloakUser>[] = [
    {
      key: 'username',
      label: 'Username',
      sortable: true,
      render: (item) => <span className="font-medium">{item.username}</span>,
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      render: (item) => <span className="text-muted-foreground">{item.email || '—'}</span>,
    },
    {
      key: 'firstName',
      label: 'First Name',
      sortable: true,
      render: (item) => <span>{item.firstName || '—'}</span>,
    },
    {
      key: 'lastName',
      label: 'Last Name',
      sortable: true,
      render: (item) => <span>{item.lastName || '—'}</span>,
    },
    {
      key: 'clientName',
      label: 'Assigned client',
      sortable: false,
      render: (item) => <span className="text-muted-foreground">{item.clientName ?? '—'}</span>,
    },
    {
      key: 'roles',
      label: 'Access Level',
      render: (item) => (
        <Badge variant="outline" className="text-xs">
          {ACCESS_LEVEL_LABEL[resolveAccessLevel(item.roles)]}
        </Badge>
      ),
    },
    {
      key: 'enabled',
      label: 'Status',
      render: (item) => (
        <Badge variant={item.enabled ? 'default' : 'destructive'}>
          {item.enabled ? 'Active' : 'Disabled'}
        </Badge>
      ),
    },
    {
      key: 'createdTimestamp',
      label: 'Created',
      sortable: true,
      align: 'right' as const,
      render: (item) => (
        <span className="text-muted-foreground">{formatDate(item.createdTimestamp)}</span>
      ),
    },
  ];

  return (
    <DataTable
      data={data}
      columns={columns}
      caption="Users"
      isLoading={isLoading}
      error={error}
      {...(onSortChange && { onSortChange })}
      {...(onPageChange && { onPageChange })}
      {...(onUserClick && { onRowClick: onUserClick })}
      getRowLabel={(item) => `View user ${item.username}`}
      extraFilters={searchInput}
      emptyMessage="No users found"
      loadingMessage="Loading users..."
      defaultSortKey="createdTimestamp"
      defaultSortOrder="desc"
    />
  );
};
